'use client';

import Link from 'next/link';
import { useAuth } from '@/lib/auth-context';

interface SensitiveOverlayProps {
  isSensitive: boolean;
  href: string;
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'yellow';
}

export default function SensitiveOverlay({
  isSensitive,
  href,
  children,
  className = '',
  variant = 'default'
}: SensitiveOverlayProps) {
  const { role, showSensitive } = useAuth();
  const canViewSensitive = role === 'admin' || role === 'trusted';

  if (!isSensitive) {
    return (
      <Link href={href} className={className}>
        {children}
      </Link>
    );
  }

  // When toggle is on, show content with a left stripe indicator
  if (showSensitive && canViewSensitive) {
    return (
      <Link href={href} className={className} style={{ borderLeft: '4px solid #a1a1aa' }}>
        {children}
      </Link>
    );
  }

  const overlayStyles = {
    default: {
      bg: 'bg-black/5 dark:bg-white/5',
      text: 'text-zinc-400 dark:text-zinc-500',
      labelBg: 'bg-white/80 dark:bg-zinc-900/80',
    },
    yellow: {
      bg: 'bg-yellow-900/10 dark:bg-yellow-100/10',
      text: 'text-yellow-200 dark:text-yellow-800',
      labelBg: 'border border-yellow-200 dark:border-yellow-800',
    },
  };

  const styles = overlayStyles[variant];

  // Admins and delegates can click through to view sensitive items
  if (canViewSensitive) {
    return (
      <Link href={href} className={`${className} relative`}>
        <div className="blur-sm pointer-events-none select-none">
          {children}
        </div>
        <div className={`absolute inset-0 flex items-center justify-center ${styles.bg} rounded-lg`}>
          <span className={`${styles.text} font-medium text-sm px-3 py-1 ${styles.labelBg} rounded`}>
            Sensitive
          </span>
        </div>
      </Link>
    );
  }

  // Viewers see a non-clickable overlay
  return (
    <div className={`${className} relative cursor-not-allowed`}>
      <div className="blur-sm pointer-events-none select-none">
        {children}
      </div>
      <div className={`absolute inset-0 flex items-center justify-center ${styles.bg} rounded-lg`}>
        <span className={`${styles.text} font-medium text-sm px-3 py-1 ${styles.labelBg} rounded`}>
          Sensitive
        </span>
      </div>
    </div>
  );
}
